import { supabase } from '@/lib/supabase'

export type TF = '5'|'15'|'30'|'60'|'240'|'720'|'D'|'W'|'M'

export type TrendConfig = {
  formaA: {
    enabled?: boolean
    bullish: Record<TF, TF | null> | Record<string, string | null>
    bearish: Record<TF, TF | null> | Record<string, string | null>
  }
  formaB: {
    enabled: Partial<Record<TF, boolean>> | Record<string, boolean>
    epsilon: number
    liveEnabled?: boolean
    livePersistence?: Record<string, number>
    liveEpsilonPct?: Record<string, number>
  }
  retargetDominantEnabled?: boolean
}

export const DEFAULT_FORMA_A_MAP: Record<TF, TF> = {
  '5': '5',   // not used (self), kept for completeness
  '15': '5',
  '30': '15',
  '60': '15',
  '240': '60',
  '720': '240',
  'D': '240',
  'W': 'D',
  'M': 'W',
}

export const DEFAULT_FORMA_B_ENABLED: Partial<Record<TF, boolean>> = {
  '60': true,
  '240': true,
  '720': true,
  'D': true,
  'W': true,
  'M': true,
}

const K_FORMA_A_BULLISH = 'ops.formaA.map.bullish'
const K_FORMA_A_BEARISH = 'ops.formaA.map.bearish'
const K_FORMA_A_ENABLED = 'ops.formaA.enabled'
const K_FORMA_B_ENABLED = 'ops.formaB.enabled'
const K_FORMA_B_EPSILON = 'ops.formaB.epsilon'
const K_RETARGET_DOM = 'ops.retarget_to_dominant_enabled'
const K_FB_LIVE_ENABLED = 'ops.formaB.live_anticipation_enabled'
const K_FB_LIVE_PERSISTENCE = 'ops.formaB.live_persistence'
const K_FB_LIVE_EPSILON = 'ops.formaB.live_epsilon_pct'

function safeJsonParse<T>(s?: string | null): T | undefined {
  if (!s) return undefined
  try { return JSON.parse(s) as T } catch { return undefined }
}

export async function getTrendConfig(): Promise<TrendConfig> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('key,value')
    .in('key', [K_FORMA_A_BULLISH, K_FORMA_A_BEARISH, K_FORMA_A_ENABLED, K_FORMA_B_ENABLED, K_FORMA_B_EPSILON, K_RETARGET_DOM, K_FB_LIVE_ENABLED, K_FB_LIVE_PERSISTENCE, K_FB_LIVE_EPSILON])

  if (error) {
    // return defaults on read error
    return {
      formaA: { bullish: { ...DEFAULT_FORMA_A_MAP }, bearish: { ...DEFAULT_FORMA_A_MAP } },
      formaB: { enabled: { ...DEFAULT_FORMA_B_ENABLED }, epsilon: 0 },
    }
  }

  const map = new Map<string, string>((data || []).map((r: any) => [String(r.key), String(r.value ?? '')]))
  const bullish = safeJsonParse<Record<string, string | null>>(map.get(K_FORMA_A_BULLISH)) || { ...DEFAULT_FORMA_A_MAP }
  const bearish = safeJsonParse<Record<string, string | null>>(map.get(K_FORMA_A_BEARISH)) || { ...DEFAULT_FORMA_A_MAP }
  const aEnabled = (map.get(K_FORMA_A_ENABLED) ?? '').toLowerCase()
  const formaAEnabled = ['true','1','on','yes'].includes(aEnabled)
  const enabled = safeJsonParse<Record<string, boolean>>(map.get(K_FORMA_B_ENABLED)) || { ...DEFAULT_FORMA_B_ENABLED }
  const epsilonStr = map.get(K_FORMA_B_EPSILON)
  const epsilon = epsilonStr ? Number(epsilonStr) : 0
  const retargetDom = (map.get(K_RETARGET_DOM) ?? '').toLowerCase()
  const fbLiveEnabled = (map.get(K_FB_LIVE_ENABLED) ?? '').toLowerCase()
  const fbLivePersistence = safeJsonParse<Record<string, number>>(map.get(K_FB_LIVE_PERSISTENCE)) || {}
  const fbLiveEps = safeJsonParse<Record<string, number>>(map.get(K_FB_LIVE_EPSILON)) || {}

  return {
    formaA: { bullish, bearish, enabled: formaAEnabled },
    formaB: { enabled, epsilon: Number.isFinite(epsilon) ? epsilon : 0, liveEnabled: ['true','1','on','yes'].includes(fbLiveEnabled), livePersistence: fbLivePersistence, liveEpsilonPct: fbLiveEps },
    retargetDominantEnabled: ['true','1','on','yes'].includes(retargetDom),
  }
}

export async function setTrendConfig(cfg: TrendConfig): Promise<void> {
  const rows = [
    { key: K_FORMA_A_BULLISH, value: JSON.stringify(cfg.formaA.bullish || {}) },
    { key: K_FORMA_A_BEARISH, value: JSON.stringify(cfg.formaA.bearish || {}) },
    { key: K_FORMA_A_ENABLED, value: (cfg.formaA.enabled ? 'true' : 'false') },
    { key: K_FORMA_B_ENABLED, value: JSON.stringify(cfg.formaB.enabled || {}) },
    { key: K_FORMA_B_EPSILON, value: String(cfg.formaB.epsilon ?? 0) },
    { key: K_RETARGET_DOM, value: (cfg.retargetDominantEnabled ? 'true' : 'false') },
    { key: K_FB_LIVE_ENABLED, value: (cfg.formaB.liveEnabled ? 'true' : 'false') },
    { key: K_FB_LIVE_PERSISTENCE, value: JSON.stringify(cfg.formaB.livePersistence || {}) },
    { key: K_FB_LIVE_EPSILON, value: JSON.stringify(cfg.formaB.liveEpsilonPct || {}) },
  ]
  const { error } = await supabase.from('app_settings').upsert(rows as any, { onConflict: 'key' })
  if (error) throw error
}

export function getDefaults(): TrendConfig {
  return {
    formaA: { bullish: { ...DEFAULT_FORMA_A_MAP }, bearish: { ...DEFAULT_FORMA_A_MAP } },
    formaB: { enabled: { ...DEFAULT_FORMA_B_ENABLED }, epsilon: 0 },
  }
}
